import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { isMobile } from "../../globalStyles/mobile";
import { supplierService } from "../../utils/services/supplierService";
import AddressLocationFields from "../AddressLocation";
import { fieldSx } from "../../pages/manage-catalog/constants";

const SupplierModal = ({
  open,
  handleClose,
  onAddData,
  selectedSupplier,
  clientId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
  });

  const [addressData, setAddressData] = useState({
    address1: "",
    address2: "",
    city: "",
    province: "",
    province_code: "",
    country: "",
    country_code: "",
    zipcode: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};

    if (!formData.name) {
      errors.name = "Name is required";
    }

    if (!formData.email) {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Enter a valid email address";
      }
    }

    if (!addressData.address1) {
      errors.address1 = "Address1 is required";
    }

    if (!addressData.province) {
      errors.province = "Province is required";
    }
    if (!addressData.country) {
      errors.country = "Country is required";
    }
    if (!addressData.province_code) {
      errors.province_code = "Province code is required";
    }
    if (!addressData.country_code) {
      errors.country_code = "Country code is required";
    }
    if (!addressData.zipcode) {
      errors.zip = "Zipcode is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    setAddressData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (selectedSupplier) {
      setFormData({
        name: selectedSupplier?.name || "",
        email: selectedSupplier?.email || "",
      });

      setAddressData({
        address1: selectedSupplier?.address?.address1 ?? "",
        address2: selectedSupplier?.address?.address2 ?? "",
        city: selectedSupplier?.address?.city ?? "",
        province: selectedSupplier?.address?.province ?? "",
        province_code: selectedSupplier?.address?.province_code ?? "",
        country: selectedSupplier?.address?.country ?? "",
        country_code: selectedSupplier?.address?.country_code ?? "",
        zipcode: selectedSupplier?.address?.zipcode ?? "",
      });
    } else {
      initialData();
    }
  }, [selectedSupplier]);
  const initialData = () => {
    setFormData({ name: "", email: "" });
    setAddressData({
      address1: "",
      address2: "",
      city: "",
      province: "",
      country: "",
      zipcode: "",
      country_code: "",
      province_code: "",
    });
    setFormErrors({});
  };

  const handleEditData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      const supplierId = selectedSupplier?._id;
      await supplierService.edit(supplierId, {
        name: formData.name,
        email: formData.email,
        address: addressData,
      });

      onAddData && onAddData();
      toast.success("Supplier edited successfully!");
      initialData();
      handleClose();
    } catch (e) {
      toast.error("Failed to edit supplier.");
      console.error(e);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const requestData = {
        name: formData.name,
        email: formData.email,
        address: addressData,
        client: clientId,
      };

      await supplierService.create(requestData);

      onAddData && onAddData();
      toast.success("Supplier created successfully!");
      handleClose();
      initialData();
    } catch (e) {
      console.error("Error creating supplier:", e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        initialData();
      }}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle
          id="form-dialog-title"
          style={{
            fontFamily: "Poppins",
            fontSize: isMobile() ? 16 : 24,
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {selectedSupplier ? "Edit" : "Create"} Supplier
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CustomTypography
              fontSize="14px"
              fontWeight={400}
              fontFamily={"poppins"}
            >
              Name <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              name="name"
              autoFocus
              size="small"
              placeholder="Enter Name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              margin="dense"
              error={!!formErrors.name}
              helperText={formErrors.name}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              Email <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              sx={{ ...fieldSx, marginTop: 1 }}
              name="email"
              type="email"
              size="small"
              placeholder="Enter Email"
              variant="outlined"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              autoFocus
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>
          <AddressLocationFields
            optionalFields={["city"]}
            address={{
              city: addressData.city,
              province: addressData.province,
              province_code: addressData.province_code,
              country: addressData.country,
              country_code: addressData.country_code,
              zip: addressData.zipcode,
            }}
            errors={formErrors}
            onZipChange={(zip) =>
              setAddressData((prev) => ({ ...prev, zipcode: zip }))
            }
            onCityChange={(city) =>
              setAddressData((prev) => ({ ...prev, city }))
            }
            onProvinceChange={(province) =>
              setAddressData((prev) => ({
                ...prev,
                province: province?.name || "",
                province_code: province?.code || "",
              }))
            }
            onProvinceSelect={(province) =>
              setAddressData((prev) => ({
                ...prev,
                province: province?.name || "",
                province_code: province?.code || "",
              }))
            }
            onCountryChange={(country) =>
              setAddressData((prev) => ({
                ...prev,
                country: country?.name || "",
                country_code: country?.code2 || "",
              }))
            }
          />
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>
              Address 1 <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              name="address1"
              margin="dense"
              type="address"
              size="small"
              placeholder="Enter Address 1"
              variant="outlined"
              value={addressData.address1}
              onChange={handleAddressChange}
              fullWidth
              autoFocus
              error={!!formErrors.address1}
              helperText={formErrors.address1}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomTypography fontSize={15}>Address 2</CustomTypography>
            <TextField
              name="address2"
              margin="dense"
              type="address"
              size="small"
              placeholder="Enter Address 2"
              variant="outlined"
              value={addressData.address2}
              onChange={handleAddressChange}
              fullWidth
              autoFocus
              error={!!formErrors.address2}
              helperText={formErrors.address2}
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ paddingBottom: 15, paddingRight: 15 }}>
        <Button
          style={{
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
          }}
          variant="outlined"
          onClick={() => {
            handleClose();
            initialData();
          }}
          color="primary"
        >
          Cancel
        </Button>
        <Button
          onClick={(e) =>
            selectedSupplier ? handleEditData(e) : handleSave(e)
          }
          color="primary"
          variant="contained"
          style={{
            color: "#FFFFFF",
            fontFamily: "Poppins",
            fontSize: 14,
            fontWeight: 600,
            width: 100,
            textTransform: "none",
          }}
        >
          {selectedSupplier ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierModal;
